import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { fetchTreatmentsByNames } from '@/utils/supabase/queryHelpers';

type Treatment = Database['public']['Tables']['treatments']['Row'];
type Provider = Database['public']['Tables']['providers']['Row'];

/**
 * Build provider queries for a given treatment
 */
const buildProviderQueries = (treatment: Treatment) => {
  const providerQueries = [];

  if (treatment.name) {
    providerQueries.push(
      supabase.from('providers').select('*').contains('available_treatments', [treatment.name])
    );
  }

  if (treatment.clinic_id) {
    providerQueries.push(
      supabase.from('providers').select('*').eq('id', treatment.clinic_id).maybeSingle()
    );
  }

  if (treatment.dedicated_provider_id) {
    providerQueries.push(
      supabase.from('providers').select('*').eq('id', treatment.dedicated_provider_id).maybeSingle()
    );
  }

  return providerQueries;
};

/**
 * Execute provider queries and deduplicate results
 */
const executeProviderQueries = async (queries: unknown[]): Promise<Provider[]> => {
  const providerResults = await Promise.all(
    queries.map(async q => {
      const result = await (q as Promise<{ data: unknown; error: unknown }>);
      if (result.error) {
        throw result.error instanceof Error ? result.error : new Error('Database query failed');
      }
      return result.data ?? null;
    })
  );

  const providersWithTreatment: Provider[] = [];
  const seenProviderIds = new Set<string>();

  const processProvider = (provider: unknown) => {
    if (
      provider &&
      typeof provider === 'object' &&
      'id' in provider &&
      typeof (provider as { id: unknown }).id === 'string' &&
      !seenProviderIds.has((provider as { id: string }).id)
    ) {
      seenProviderIds.add((provider as { id: string }).id);
      providersWithTreatment.push(provider as Provider);
    }
  };

  for (const result of providerResults) {
    if (!result) continue;

    if (Array.isArray(result)) {
      for (const provider of result) {
        processProvider(provider);
      }
    } else {
      processProvider(result);
    }
  }

  return providersWithTreatment;
};

/**
 * Load doctors for specific provider IDs
 */
const loadDoctorsForProviders = async (providerIds: string[]) => {
  if (providerIds.length === 0) return [];

  // Build OR query for all provider IDs across clinic_id, provider_id, and selected_clinics
  const orClauses = [];
  for (const id of providerIds) {
    orClauses.push(`clinic_id.eq.${id}`);
    orClauses.push(`provider_id.eq.${id}`);
    orClauses.push(`selected_clinics.cs.{${id}}`);
  }

  const orConditions = orClauses.join(',');

  if (!orConditions) return [];

  const { data: doctorsData, error: dErr } = await supabase
    .from('doctors')
    .select('*')
    .or(orConditions);
  if (dErr) throw dErr;
  return doctorsData;
};

/**
 * Check if a string looks like a full UUID
 */
const isFullUUID = (str: string): boolean => {
  if (str.length !== 36) return false;
  const parts = str.split('-');
  if (parts.length !== 5) return false;
  return parts.every(
    part => /^[\da-f]+$/i.test(part) && [8, 4, 4, 4, 12][parts.indexOf(part)] === part.length
  );
};

/**
 * Try to find provider by direct ID lookup
 */
const findProviderByDirectId = async (providerId: string) => {
  try {
    const { data: directProvider, error: dirErr } = await supabase
      .from('providers')
      .select('*')
      .eq('id', providerId)
      .maybeSingle();
    if (dirErr) throw dirErr;
    return directProvider;
  } catch {
    return null;
  }
};

/**
 * Try to find provider by unique_code lookup
 */
const findProviderByUniqueCode = async (uniqueCode: string) => {
  try {
    const { data: providerByCode, error: codeErr } = await supabase
      .from('providers')
      .select('*')
      .eq('unique_code', uniqueCode)
      .maybeSingle();
    return !codeErr && providerByCode ? providerByCode : null;
  } catch {
    return null;
  }
};

/**
 * Try to find provider by prefix matching
 */
const findProviderByPrefix = async (providerIdPrefix: string) => {
  // Only run if prefix looks like a UUID fragment
  if (providerIdPrefix.length < 8) return null;

  try {
    // Use a database function for better performance if available
    // Otherwise, fetch only the first 100 providers to reduce load
    const { data: limitedProviders, error: lErr } = await supabase
      .from('providers')
      .select('id')
      .order('created_at', { ascending: false })
      .limit(100);

    if (lErr) throw lErr;

    // Find matching provider by prefix
    const matchingProvider = limitedProviders.find(p =>
      String(p.id).toLowerCase().startsWith(providerIdPrefix.toLowerCase())
    );

    if (matchingProvider) {
      // Now fetch the full provider data
      const { data: fullProvider, error: fErr } = await supabase
        .from('providers')
        .select('*')
        .eq('id', matchingProvider.id)
        .single();
      if (fErr) throw fErr;
      return fullProvider;
    }
  } catch {
    // Prefix matching failed
  }

  return null;
};

// Load providers for a specific treatment
export const loadProvidersForTreatment = async (treatmentId: string) => {
  // Step 1: Get the specific treatment
  const { data: treatment, error: tErr } = await supabase
    .from('treatments')
    .select('*')
    .eq('id', treatmentId)
    .single();
  if (tErr) {
    throw tErr;
  }

  // Step 2: Query providers that offer this treatment using targeted lookups
  const providerQueries = buildProviderQueries(treatment);
  const providersWithTreatment = await executeProviderQueries(providerQueries);

  // Step 3: Load only doctors for these specific providers
  const providerIds = providersWithTreatment.map(p => (p as { id: string }).id);
  const doctors = await loadDoctorsForProviders(providerIds);

  return {
    treatment,
    providers: providersWithTreatment,
    doctors: doctors,
  };
};

// Load a single provider by ID or ID prefix
export const loadSingleProvider = async (providerIdPrefix: string) => {
  // Step 1: Try direct ID lookup if it looks like a full UUID
  if (isFullUUID(providerIdPrefix)) {
    const provider = await findProviderByDirectId(providerIdPrefix);
    if (provider) return provider;
  }

  // Step 2: Try unique_code lookup
  const providerByCode = await findProviderByUniqueCode(providerIdPrefix);
  if (providerByCode) return providerByCode;

  // Step 3: Fallback to prefix matching
  const providerByPrefix = await findProviderByPrefix(providerIdPrefix);

  if (!providerByPrefix) {
    throw new Error('Provider not found');
  }

  return {
    provider: providerByPrefix,
  };
};

// Helper function to deduplicate treatments
const deduplicateTreatments = (
  treatmentsData: Database['public']['Tables']['treatments']['Row'][],
  treatmentsByNameData: Database['public']['Tables']['treatments']['Row'][]
): Database['public']['Tables']['treatments']['Row'][] => {
  const allTreatments = [...treatmentsData];
  if (treatmentsByNameData.length > 0) {
    const existingIds = new Set(allTreatments.map(t => t.id));
    for (const t of treatmentsByNameData) {
      if (!existingIds.has(String(t.id))) {
        allTreatments.push(t);
      }
    }
  }
  return allTreatments;
};

// Helper function to fetch related data
const fetchRelatedData = async (
  providerId: string,
  validTreatmentNames: string[],
  options: { limitTreatments?: number; limitDoctors?: number }
): Promise<{
  treatmentsData: Database['public']['Tables']['treatments']['Row'][];
  doctorsData: Database['public']['Tables']['doctors']['Row'][];
  treatmentsByNameData: Database['public']['Tables']['treatments']['Row'][];
}> => {
  const treatmentsByNamePromise =
    validTreatmentNames.length > 0
      ? fetchTreatmentsByNames<Database['public']['Tables']['treatments']['Row']>(
          supabase,
          validTreatmentNames
        ).catch(() => [])
      : Promise.resolve([]);

  const [treatmentsData, doctorsData, treatmentsByNameData] = await Promise.all([
    supabase
      .from('treatments')
      .select('*')
      .or(`clinic_id.eq.${providerId},dedicated_provider_id.eq.${providerId}`)
      .limit(options.limitTreatments ?? 50)
      .then(({ data, error }) => (error ? [] : data)),
    supabase
      .from('doctors')
      .select('*')
      .or(
        `clinic_id.eq.${providerId},provider_id.eq.${providerId},selected_clinics.cs.{${providerId}}`
      )
      .limit(options.limitDoctors ?? 50)
      .then(({ data, error }) => (error ? [] : data)),
    treatmentsByNamePromise,
  ]);

  return {
    treatmentsData,
    doctorsData,
    treatmentsByNameData,
  };
};

export const loadProviderRelations = async (
  providerId: string,
  availableTreatmentNames: string[] = [],
  options: { limitTreatments?: number; limitDoctors?: number } = {}
) => {
  if (!providerId) {
    throw new Error('Provider ID is required to load relations');
  }

  // Fetch the provider data
  const { data: foundProvider, error: providerError } = await supabase
    .from('providers')
    .select('*')
    .eq('id', providerId)
    .single();

  if (providerError) throw providerError;

  const validTreatmentNames = Array.isArray(availableTreatmentNames)
    ? availableTreatmentNames.filter(
        name => name && typeof name === 'string' && name.trim().length > 0
      )
    : [];

  const { treatmentsData, doctorsData, treatmentsByNameData } = await fetchRelatedData(
    providerId,
    validTreatmentNames,
    options
  );

  const allTreatments = deduplicateTreatments(treatmentsData, treatmentsByNameData);

  return {
    provider: foundProvider,
    treatments: allTreatments,
    doctors: doctorsData,
  };
};

// Type for preview provider data
export interface PreviewProviderData {
  id?: string;
  name?: string;
  name_en?: string;
  description?: string;
  location?: string;
  image_urls?: string[];
  specialties?: string[];
  unique_code?: string;
  treatments?: unknown[];
  doctors?: unknown[];
  [key: string]: unknown;
}

// Load preview provider data from sessionStorage
export const loadPreviewProvider = (previewId: string) => {
  // Check both sessionStorage and localStorage for preview data (for cross-tab support)
  let previewData = sessionStorage.getItem(previewId);
  previewData ??= localStorage.getItem(previewId);

  if (!previewData) {
    // TEMP: Return mock data for testing
    return {
      provider: {
        id: 'test-preview-id',
        name: 'Test Preview Provider',
        name_en: 'Test Preview Provider',
        description: 'This is a test preview provider',
        location: 'Test Location',
        image_urls: [],
        specialties: ['Test Specialty'],
        unique_code: 'test-preview',
      },
      treatments: [],
      doctors: [],
    };

    // If preview data is not found in either storage, provide a more helpful error
    // This can happen if the error boundary cleared storage or the preview session expired
    // throw new Error(`Preview data not found for ID: ${previewId}. Please try creating a new preview.`);
  }

  const parsedData = JSON.parse(previewData) as PreviewProviderData;

  // For preview mode, use the data from the preview instead of loading from database
  // This avoids issues with new providers that don't exist in the database yet
  const treatments = parsedData.treatments ?? [];
  const doctors = parsedData.doctors ?? [];

  return {
    provider: parsedData,
    treatments: treatments,
    doctors: doctors,
  };
};
